import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2, Plus } from "lucide-react";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { useCompare, MAX_COMPARE } from "@/context/CompareContext";
import { SelectButton } from "@/components/SelectButton";
import { Eyebrow, EASE } from "@/components/Reveal";

const Swatch = ({ item, onRemove, onZoom, i }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
    className="min-w-0 flex flex-col" data-testid={`compare-col-${item.id}`}>
    <div className="relative h-[52vh] sm:h-[68vh] overflow-hidden border border-[#D4AF37]/20 bg-[#13141C] group">
      <img src={imgUrl(item.images?.[0])} alt={item.name} className="w-full h-full object-cover" />
      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-col gap-2">
        <button onClick={onRemove} aria-label="Remove" data-testid={`compare-remove-${item.id}`}
          className="w-9 h-9 grid place-items-center rounded-full bg-[#0D0D11]/70 backdrop-blur text-[#F5F3EF] hover:text-red-400"><X className="w-4 h-4" /></button>
        <button onClick={onZoom} aria-label="View full screen" data-testid={`compare-zoom-${item.id}`}
          className="w-9 h-9 grid place-items-center rounded-full bg-[#0D0D11]/70 backdrop-blur text-[#E5C158]"><Maximize2 className="w-4 h-4" /></button>
      </div>
    </div>
    <div className="mt-4">
      <p className="font-mono-j text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] truncate">{item.supplier}</p>
      <Link to={`/item/${item.id}`} className="block mt-1 font-display text-lg sm:text-3xl text-[#F5F3EF] leading-tight break-words hover:text-[#E5C158] transition-colors">{item.name}</Link>
      <div className="mt-3 hidden sm:block"><SelectButton item={item} variant="full" /></div>
    </div>
  </motion.div>
);

export default function ComparePage() {
  const { ids, remove, category, noun } = useCompare();
  const [zoom, setZoom] = useState(null);
  const { data } = useSWR(category ? `/items?category=${category}` : null, fetcher);
  const browse = `/c/${category || "board-products"}`;
  const items = ids.map((id) => asList(data).find((i) => i.id === id)).filter(Boolean);
  const labels = [...new Set(items.flatMap((i) => asList(i.specs).map((s) => s.label)))].filter((l) => l !== "Brand");
  const rows = [["Code", (i) => i.code], ["Type", (i) => i.style], ...labels.map((l) => [l, (i) => asList(i.specs).find((s) => s.label === l)?.value])];
  const cols = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" }[Math.max(1, items.length)];

  return (
    <main className="pt-28 mx-auto max-w-[1600px] px-3 sm:px-10 min-h-screen">
      <div className="px-2 sm:px-0 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Side by side · {items.length} of {MAX_COMPARE}</Eyebrow>
          <h1 className="mt-4 font-display font-light text-5xl sm:text-7xl text-[#F5F3EF]">Compare {noun}</h1>
        </div>
        {items.length < MAX_COMPARE && (
          <Link to={browse} data-testid="compare-add-more" className="inline-flex items-center gap-2 nav-link font-mono-j text-xs uppercase tracking-[0.25em] text-[#E5C158]">
            <Plus className="w-4 h-4" /> Add {noun === "stones" ? "a stone" : "a board"}
          </Link>
        )}
      </div>

      {items.length === 0 ? (
        <div className="mt-16 border border-dashed border-[#D4AF37]/30 p-12 text-center" data-testid="compare-empty">
          <p className="font-display italic text-3xl text-[#A09E97]">Nothing to compare yet.</p>
          <p className="mt-3 text-sm text-[#A09E97]">Tap the compare icon on up to three boards or stones.</p>
          <div className="mt-6 flex justify-center gap-8">
            <Link to="/c/board-products" className="nav-link font-mono-j text-xs uppercase tracking-[0.25em] text-[#E5C158]">Board products</Link>
            <Link to="/c/stone-options" className="nav-link font-mono-j text-xs uppercase tracking-[0.25em] text-[#E5C158]">Stone options</Link>
          </div>
        </div>
      ) : (
        <>
          <div className={`mt-10 grid ${cols} gap-2 sm:gap-6`} data-testid="compare-grid">
            {items.map((it, i) => <Swatch key={it.id} item={it} i={i} onRemove={() => remove(it.id)} onZoom={() => setZoom(it)} />)}
          </div>
          <div className="mt-12 border-t border-[#D4AF37]/20" data-testid="compare-specs">
            {rows.map(([label, get]) => (
              <div key={label} className="py-4 border-b border-[#D4AF37]/10">
                <p className="px-2 sm:px-0 font-mono-j text-[11px] uppercase tracking-[0.2em] text-[#A09E97] mb-2">{label}</p>
                <div className={`grid ${cols} gap-2 sm:gap-6`}>
                  {items.map((it) => <p key={it.id} className="px-2 sm:px-0 text-sm sm:text-base text-[#F5F3EF] break-words">{get(it) || "—"}</p>)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <AnimatePresence>
        {zoom && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setZoom(null)} data-lenis-prevent
            className="fixed inset-0 z-[60] bg-[#0D0D11]/95 backdrop-blur-xl p-4 sm:p-10 flex flex-col" data-testid="compare-zoom-overlay">
            <div className="flex justify-between items-center mb-4">
              <p className="font-display text-2xl sm:text-4xl text-[#F5F3EF]">{zoom.name} <span className="font-mono-j text-xs text-[#D4AF37] ml-2">{zoom.supplier}</span></p>
              <button aria-label="Close" data-testid="compare-zoom-close" className="w-11 h-11 grid place-items-center rounded-full border border-[#D4AF37]/40 text-[#F5F3EF]"><X className="w-5 h-5" /></button>
            </div>
            <img src={imgUrl(zoom.images?.[0])} alt={zoom.name} className="flex-1 min-h-0 w-full object-contain" />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
